import React, { useEffect } from "react";

import DatatablePage from "../../../layout/datatable-pages/companies";
import { fetchData, getCompaniesFieldsArray } from "../../../utils/helpers";
import { props } from "../../datatable/props";
import useDatatable from "../../hooks/useDatatable";

import "../datatable-pages.css";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * ActiveCompanies Component
 * 
 * Displays a paginated datatable of active companies with extensive business information.
 * Users can browse and search through 25+ company attributes including financials, 
 * management, investors, and operational details. Shows statistics on active companies 
 * and companies with unknown status. Requires user authentication to access.
 * 
 * @returns {JSX.Element} Datatable page with active companies data
 */
const ActiveCompanies = () => {
  // Array of field keys to display in the datatable
  // Each key corresponds to a company data attribute
  const fieldKeys = [
    "tradeName",
    "stageOfDevelopment",
    "industry",
    "description",
    "categories",
    "location",
    "ownership",
    "businessModelType",
    "defensibilityMoat",
    "productReview",
    "lastFinancingRound",
    "totalInvestment",
    "postMoneyValuation",
    "lastFinancingDate",
    "numOfFundingRounds",
    "numOfInvestors",
    "investors",
    "competitors",
    "similarEntities",
    "management",
    "numOfEmployees",
    "phoneNumber",
    "operationalDuration",
    "foundedDate",
    "pitchdeck_show",
  ];

  // Convert field keys to formatted field objects with labels and types
  const fields = getCompaniesFieldsArray(fieldKeys);

  // Get datatable state and props from custom hook
  const { datatableProps } = useDatatable();

  // Get Supabase utilities and user authentication context
  const { currentUser, supabase, getUserTier } = useSupabaseApp();

  // Verify user is authenticated before rendering
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // Configuration object for datatable fetch and display
  // Includes API function name, collection, title, statistics, and field configuration
  const params = {
    supabase,
    ...datatableProps,
    getData: "getActiveCompanies",
    collection: "companies",
    title: "Companies",
    datatipText:
      "Understand the business offerings in your competitive landscape by browsing through our collection of active companies.",
    statsArray: [
      {
        actualName: "activeCompanies",
        displayName: "Active companies",
        total: "779,113",
      },
      {
        actualName: "statusUnknown",
        displayName: "Status unknown",
        total: "42,422",
      },
    ],
    placeholderText: "Search from active companies",
    fields: fields,
    getCompanyDetailsForExpand: "getCompanyDetailsForExpand",
  };

  // Fetch company data on component mount
  useEffect(() => {
    fetchData(params);
  }, []);

  // Render datatable page with configured params
  return <DatatablePage props={props(params)} />;
};

export default ActiveCompanies;
